import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Layout from './components/layout/Layout';
import HomePage from './pages/HomePage';
import AboutPage from './pages/AboutPage';
import ServicesPage from './pages/ServicesPage';
import FAQPage from './pages/FAQPage';
import ContactPage from './pages/ContactPage';
// Service pages
import MajorMedicalPage from './pages/services/MajorMedicalPage';
import LifeInsurancePage from './pages/services/LifeInsurancePage';
import WealthArchitecturePage from './pages/services/WealthArchitecturePage';
import TravelInsurancePage from './pages/services/TravelInsurancePage';
import BondsPage from './pages/services/BondsPage';
import OtherInsurancePage from './pages/services/OtherInsurancePage';
import HomeInsurancePage from './pages/services/HomeInsurancePage';
import AutoInsurancePage from './pages/services/AutoInsurancePage';
// Forms pages
import FormsPage from './pages/FormsPage';
import HomeInsuranceFormPage from './pages/forms/HomeInsuranceFormPage';
// Legal
import PrivacyPage from './pages/PrivacyPage';

// Component to handle language detection and switching
const LanguageHandler: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const location = useLocation();
  const { i18n } = useTranslation();

  useEffect(() => {
    // Detect language from URL
    const isEnglish = location.pathname.startsWith('/en');
    const currentLang = isEnglish ? 'en' : 'es';

    if (i18n.language !== currentLang) {
      i18n.changeLanguage(currentLang);
    }
  }, [location.pathname, i18n]);

  return <>{children}</>;
};

// Scroll to top on route change
const ScrollToTop: React.FC = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
};

function App() {
  return (
    <BrowserRouter>
      <LanguageHandler>
        <ScrollToTop />
        <Routes>
          {/* Spanish Routes (Default) */}
          <Route path="/" element={<Layout />}>
            <Route index element={<HomePage />} />
            <Route path="nosotros" element={<AboutPage />} />
            <Route path="equipo" element={<Navigate to="/nosotros" replace />} />
            <Route path="servicios" element={<ServicesPage />} />
            <Route path="servicios/gastos-medicos-mayores" element={<MajorMedicalPage />} />
            <Route path="servicios/seguro-de-vida" element={<LifeInsurancePage />} />
            <Route path="servicios/arquitectura-patrimonial" element={<WealthArchitecturePage />} />
            <Route path="servicios/riesgos-turisticos" element={<TravelInsurancePage />} />
            <Route path="servicios/fianzas" element={<BondsPage />} />
            <Route path="servicios/otros-seguros" element={<OtherInsurancePage />} />
            <Route path="servicios/seguro-de-hogar" element={<HomeInsurancePage />} />
            <Route path="servicios/seguro-de-auto" element={<AutoInsurancePage />} />
            <Route path="faq" element={<FAQPage />} />
            <Route path="preguntas-frecuentes" element={<Navigate to="/faq" replace />} />
            <Route path="contacto" element={<ContactPage />} />
            <Route path="aviso-de-privacidad" element={<PrivacyPage />} />
            {/* WIP forms — live in production but unlinked; reachable only by direct
                URL (/wip/formularios...). To launch publicly, move these to
                /formularios and restore the nav link (see Header.tsx). */}
            <Route path="wip/formularios" element={<FormsPage />} />
            <Route path="wip/formularios/seguro-de-hogar" element={<HomeInsuranceFormPage />} />
          </Route>

          {/* English Routes */}
          <Route path="/en" element={<Layout />}>
            <Route index element={<HomePage />} />
            <Route path="about" element={<AboutPage />} />
            <Route path="team" element={<Navigate to="/en/about" replace />} />
            <Route path="services" element={<ServicesPage />} />
            <Route path="services/major-medical" element={<MajorMedicalPage />} />
            <Route path="services/life-insurance" element={<LifeInsurancePage />} />
            <Route path="services/wealth-architecture" element={<WealthArchitecturePage />} />
            <Route path="services/travel-insurance" element={<TravelInsurancePage />} />
            <Route path="services/bonds" element={<BondsPage />} />
            <Route path="services/other-insurance" element={<OtherInsurancePage />} />
            <Route path="services/home-insurance" element={<HomeInsurancePage />} />
            <Route path="services/auto-insurance" element={<AutoInsurancePage />} />
            <Route path="faq" element={<FAQPage />} />
            <Route path="contact" element={<ContactPage />} />
            <Route path="privacy-policy" element={<PrivacyPage />} />
            {/* WIP forms — live in production but unlinked; reachable only by direct
                URL (/en/wip/forms...). See Spanish block above. */}
            <Route path="wip/forms" element={<FormsPage />} />
            <Route path="wip/forms/home-insurance" element={<HomeInsuranceFormPage />} />
          </Route>

          {/* Catch-all redirect to home */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </LanguageHandler>
    </BrowserRouter>
  );
}

export default App;
